import React, { memo, Fragment, useMemo, useCallback, useState, useRef, useEffect } from 'react';

import {
    createUseStyles,
    useTheme,
    Modal,
    Icon,
    Input,
    Advice,
    SearchBar,
    SelectWrapper,
    Chip,
    Select,
    Loader,
} from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { useWeb5Context } from 'lib/Web5Context';

import { getSelectedFilters } from '../Filters/utils';
import OptionGroup from './components/OptionGroup';
import OptionGroups from './components/OptionGroups';
import styles from './styles';
const useStyles = createUseStyles(styles, 'ModalOptions');

const ModalOptions = memo(
    ({
        advice,
        adviceType = 'info',
        className,
        onToggleModal,
        title,
        confirmText,
        cancelText,
        deleteText,
        placeholderSearch,
        isOpen,
        schema: baseSchema,
        selected,
        disabled: disabledProp,
        onChangeSelected,
        onConfirm,
        onCancel,
        size = 'medium',
        overrides,
        overlayClassName,
        postComponent,
        selectorsSchema,
        multiSchema,
        onResetSelected,
        defaultFilters,
        isLoading,
        inputFocus = true,
        forceSelectAllTypes = true,
    }) => {
        const classes = useStyles();
        const theme = useTheme();
        const classNames = classnames(classes.root, className);
        const searchContainerClassName = classnames({
            [classes.multipleSelectors]: selectorsSchema?.length,
        });
        const searchInputClassName = classnames(classes.search, {
            [classes.searchWithSelectors]: selectorsSchema?.length,
        });
        const [search, setSearch] = useState('');
        const [disabled, setDisabled] = useState(disabledProp || []);
        const [schema, setSchema] = useState(baseSchema);
        const [type, setType] = useState([]);
        const [openDropdowns, setOpenDropdowns] = useState({});
        const mappedForDisable = useRef({});
        const isFirstLoad = useRef(true);
        const selectRefs = useRef({});
        const selectorsOptions = useRef({});
        const selectorsOptionsMapped = useRef({});
        const [initialSelected, setInitialSelected] = useState({});
        const prevIsOpen = useRef({});
        const { t } = useWeb5Context();

        const setSelectedTypes = useCallback(
            ({ types, overrideState, isFirstLoad = false }) => {
                const oldSelected = { ...selected } || {};

                const newSelected = multiSchema.reduce((obj, item) => {
                    const { id } = item;

                    let isSelected;
                    if (isFirstLoad && typeof item.defaultTypeState === 'boolean') {
                        isSelected = item.defaultTypeState;
                    } else if (types) {
                        isSelected = !!types.find((type) => type.value === id);
                    } else {
                        isSelected = !!overrideState;
                    }
                    const oldSection = oldSelected[id] || {};
                    return {
                        ...obj,
                        [id]: {
                            ...oldSection,
                            isSelected: isSelected,
                        },
                    };
                }, {});

                return newSelected;
            },
            [multiSchema, selected],
        );

        const initMultiSchema = useCallback(() => {
            if (Object.keys(selected).length === 0) return;

            const hasSelectedTypes =
                Object.values(selected).filter((item) => item.isSelected).length > 0;

            const newTypes = multiSchema.reduce((arr, el) => {
                const { id, label } = el;
                if (selected[id] && selected[id].isSelected) arr.push({ label, value: id });
                return arr;
            }, []);

            setType(newTypes);

            let newSelected = selected;

            if (isFirstLoad.current) {
                newSelected =
                    !hasSelectedTypes && forceSelectAllTypes
                        ? setSelectedTypes({ types: null, overrideState: true, isFirstLoad: true })
                        : setSelectedTypes({
                              types: newTypes,
                              overrideState: false,
                              isFirstLoad: true,
                          });
                onChangeSelected({ ...newSelected });
            }

            isFirstLoad.current = false;
            return newSelected;
        }, [forceSelectAllTypes, multiSchema, onChangeSelected, selected, setSelectedTypes]);

        useEffect(() => {
            setSchema(baseSchema);
            let newSelected = selected;
            if (multiSchema) newSelected = initMultiSchema();
            if (isOpen && prevIsOpen.current !== isOpen) {
                prevIsOpen.current = true;
                setInitialSelected(newSelected);
            } else if (!isOpen) {
                prevIsOpen.current = false;
            }
        }, [baseSchema, initMultiSchema, multiSchema, selected, isOpen]);

        useEffect(() => {
            setDisabled(disabledProp || []);
        }, [disabledProp]);

        useEffect(() => {
            if (!schema && !multiSchema) return;

            const getMappedForDisable = (schema) =>
                schema.reduce((obj, group) => {
                    if (!group?.options || group?.options?.length === 0) return obj;
                    const options = group.options.reduce((object, option) => {
                        obj[option.label] = group.id;
                        return obj;
                    }, {});
                    obj = {
                        ...obj,
                        ...options,
                    };
                    return obj;
                }, {});

            if (multiSchema) {
                mappedForDisable.current = multiSchema.reduce((arr, { schema }) => {
                    return [...arr, getMappedForDisable(schema)];
                }, []);
            } else {
                mappedForDisable.current = getMappedForDisable(schema);
            }
        }, [multiSchema, schema]);

        const onSelect = useCallback(
            ({ id, parentId, options, sectionId }) => {
                return () => {
                    let newSelected = { ...selected } || {};
                    let action = '';

                    if (sectionId) {
                        newSelected = newSelected[sectionId]?.options
                            ? { ...newSelected[sectionId].options }
                            : {};
                    }

                    if (id && options && !parentId) {
                        const groupDisabledOptions = sectionId
                            ? disabled?.[sectionId]?.options?.[id]?.options
                            : disabled?.[id]?.options || [];
                        const uncheckedDisabledOptions =
                            groupDisabledOptions?.filter(
                                (option) => !newSelected[id]?.options.includes(option),
                            ) || [];
                        const checkedDisabledOptions =
                            groupDisabledOptions?.filter((option) =>
                                newSelected[id]?.options.includes(option),
                            ) || [];

                        if (!newSelected?.[id]?.isSelected) {
                            newSelected[id] = {
                                isSelected: true,
                                options: options
                                    .filter(
                                        (option) => !uncheckedDisabledOptions.includes(option.id),
                                    )
                                    .map((current) => {
                                        return current.id;
                                    }),
                            };
                            action = 'select';
                        } else {
                            newSelected[id] = {
                                isSelected: false,
                                options: checkedDisabledOptions,
                            };
                            action = 'unselect';
                        }
                    } else if (id && parentId) {
                        if (!newSelected?.[parentId]) {
                            newSelected[parentId] = {
                                isSelected: false,
                                options: [id],
                            };
                            if (options.length === newSelected[parentId].options.length) {
                                newSelected[parentId].isSelected = true;
                            }
                            action = 'select';
                        } else if (!newSelected[parentId].options.includes(id)) {
                            newSelected[parentId].options.push(id);
                            if (options.length === newSelected[parentId].options.length) {
                                newSelected[parentId].isSelected = true;
                            }
                            action = 'select';
                        } else {
                            const newOptions = newSelected[parentId].options.filter(
                                (current) => current !== id,
                            );
                            newSelected[parentId] = {
                                isSelected: false,
                                options: newOptions,
                            };
                            action = 'unselect';
                        }
                    }

                    if (sectionId) {
                        const oldSelected = { ...selected } || {};
                        const oldSection = oldSelected[sectionId] || {};
                        const newSectionObj = {
                            ...oldSelected,
                            [sectionId]: {
                                ...oldSection,
                                options: newSelected,
                            },
                        };
                        newSelected = { ...newSectionObj };
                    }

                    const optionsIds = parentId
                        ? [id]
                        : (options?.length > 0 && options.map((current) => current.id)) || [];

                    const info = {
                        clickedIds: optionsIds,
                        action,
                    };
                    onChangeSelected(newSelected, info);
                };
            },
            [selected, onChangeSelected, disabled],
        );

        const getDisableds = useCallback(
            (value) => {
                if (value) {
                    const getNewDisabled = (currentSchema) =>
                        currentSchema.reduce(
                            (obj, el) => ({
                                ...obj,
                                [el.id]: {
                                    options: disabled?.[el.id]?.options || [],
                                    isDisabled:
                                        !el.label
                                            .toLowerCase()
                                            .includes(value.trim().toLowerCase()) ||
                                        disabledProp?.[el.id]?.isDisabled,
                                },
                            }),
                            {},
                        );

                    const newDisabled = multiSchema
                        ? multiSchema.reduce(
                              (obj, el) => ({
                                  ...obj,
                                  [el.id]: {
                                      options: getNewDisabled(el.schema),
                                  },
                              }),
                              {},
                          )
                        : getNewDisabled(schema);

                    setDisabled(newDisabled);
                } else {
                    setDisabled(disabledProp || {});
                }
            },
            [disabled, disabledProp, multiSchema, schema],
        );

        const handleChangeType = useCallback(
            (idSelector) => (types) => {
                let newSelected;
                let newTypes;

                if (idSelector) {
                    // To simplify other components and props, we keep working with a single list of selected and multiSchema.
                    // Since we have a single list of selected and two or more selectors whe have to treat the new selected options.
                    const otherOptions = Object.entries(selectorsOptions.current).reduce(
                        (arr, [key, value]) => {
                            if (key !== idSelector) {
                                arr = [...arr, ...value];
                            }
                            return arr;
                        },
                        [],
                    );

                    const otherSelectedOptions = otherOptions.filter(
                        (current) => selected?.[current.value]?.isSelected || false,
                    );

                    newTypes = [...(types || []), ...otherSelectedOptions];
                    newSelected = setSelectedTypes({ types: newTypes });
                } else {
                    newSelected = setSelectedTypes({ types });
                    newTypes = types;
                }

                onChangeSelected({ ...newSelected });
                setType(newTypes);
            },
            [onChangeSelected, setSelectedTypes, selected],
        );

        const onClear = useCallback(
            (selectorId) => {
                return (e) => {
                    e.stopPropagation();
                    const newTypes = type.filter((item) => {
                        return !selectorsOptionsMapped.current?.[selectorId]?.includes(item.value);
                    });
                    const newSelected = setSelectedTypes({ types: newTypes });
                    onChangeSelected({ ...newSelected });
                    setType(newTypes);
                };
            },
            [type, setSelectedTypes, onChangeSelected],
        );

        const onSearch = useCallback(
            (value) => {
                getDisableds(value);
                setSearch(value);
            },
            [getDisableds],
        );

        const onToggle = useCallback(
            (sectionId, status) => {
                const oldSelected = { ...selected } || {};
                const oldSection = oldSelected[sectionId] || {};

                onChangeSelected({
                    ...oldSelected,
                    [sectionId]: {
                        ...oldSection,
                        isExpanded: status,
                    },
                });
            },
            [onChangeSelected, selected],
        );

        const handleToggleModal = useCallback(() => {
            setSearch('');
            setDisabled(disabledProp);
            setType([]);
            onToggleModal && onToggleModal();
        }, [disabledProp, onToggleModal]);

        const handleCancel = useCallback(() => {
            onCancel && onCancel();
            handleToggleModal();
        }, [onCancel, handleToggleModal]);

        const handleConfirm = useCallback(() => {
            onConfirm && onConfirm(selected);
            handleToggleModal();
        }, [selected, handleToggleModal, onConfirm]);

        const typeOptions = useMemo(() => {
            if (!multiSchema) return;
            return multiSchema.reduce(
                (arr, item) => [
                    ...arr,
                    {
                        label: item.label,
                        value: item.id,
                    },
                ],
                [],
            );
        }, [multiSchema]);

        const options = useMemo(() => {
            if (!schema && !multiSchema) return;

            const Options = multiSchema ? OptionGroups : OptionGroup;
            const optionsProps = {
                disabled,
                onSelect,
                search,
                selected,
            };

            if (schema) optionsProps.schema = schema;
            if (multiSchema) {
                optionsProps.multiSchema = multiSchema;
                optionsProps.onToggle = onToggle;
                optionsProps.type = type;
            }

            return <Options {...optionsProps} />;
        }, [disabled, multiSchema, onSelect, onToggle, schema, search, selected, type]);

        const selectOverrides = useMemo(() => {
            return {
                styles: {
                    menu: (base) => ({
                        ...base,
                        position: 'relative',
                        boxShadow: 'none !important',
                        margin: 0,
                        paddingBottom: 0,
                    }),
                    menuList: (base) => ({
                        ...base,
                        paddingBottom: 0,
                        ...theme.utils.scrollbar,
                    }),
                },
                option: {
                    style: {
                        borderRadius: 4,
                    },
                },
            };
        }, [theme]);

        const getIsOpenValueList = useCallback(
            (fieldId) => {
                return (isOpen) => {
                    setOpenDropdowns({ ...openDropdowns, [fieldId]: isOpen });
                    setTimeout(() => {
                        if (isOpen && selectRefs?.current?.[fieldId]) {
                            selectRefs.current[fieldId].focus();
                        }
                    }, 50);
                };
            },
            [openDropdowns, setOpenDropdowns],
        );

        const renderSelectors = useMemo(() => {
            if (!selectorsSchema?.length) return null;
            return selectorsSchema.reduce((arr, current, index) => {
                if (!current?.schema?.length) return null;
                if (current.schema) {
                    const idOptions = [];
                    const optionsFiltered = current.schema.reduce((arr, item) => {
                        idOptions.push(item.id);
                        return [
                            ...arr,
                            {
                                label: item.label,
                                value: item.id,
                                iconType: item.iconType,
                            },
                        ];
                    }, []);

                    const typesFiltered = type.filter((current) =>
                        idOptions.includes(current.value),
                    );

                    const selectorId = `selector_${index}`;
                    selectorsOptions.current[selectorId] = optionsFiltered;
                    selectorsOptionsMapped.current[selectorId] = optionsFiltered.map(
                        (current) => current.value,
                    );
                    const isOpenDropDown = openDropdowns?.[selectorId] || false;

                    const chipLabel =
                        typesFiltered.length > 0
                            ? `${current.label} (${typesFiltered.length})`
                            : current.label;

                    arr.push(
                        <div key={selectorId}>
                            <SelectWrapper
                                getIsOpen={getIsOpenValueList(selectorId)}
                                classes={{ Popover: classes.popoverCustomWide }}
                                customOptions={
                                    <Fragment>
                                        <Select
                                            options={optionsFiltered}
                                            value={typesFiltered}
                                            isMulti={true}
                                            isFuzzy={!current.selectAllLabel}
                                            isSearchable={true}
                                            isFullWidth
                                            menuPosition="absolute"
                                            useMenuPortal={false}
                                            overrides={selectOverrides}
                                            onChange={handleChangeType(selectorId)}
                                            placeholder={t('label_selectone')}
                                            isReadOnly={false}
                                            getRef={(ref) => {
                                                selectRefs.current[selectorId] = ref;
                                            }}
                                            selectAllLabel={current.selectAllLabel}
                                        />
                                    </Fragment>
                                }
                            >
                                <Chip
                                    size="large"
                                    isUnfolded={true}
                                    isActive={isOpenDropDown}
                                    isFilled={typesFiltered.length > 0}
                                    isOutlinedColor={false}
                                    className=""
                                    onRemove={typesFiltered?.length ? onClear(selectorId) : null}
                                >
                                    {chipLabel}
                                </Chip>
                            </SelectWrapper>
                        </div>,
                    );
                }

                return arr;
            }, []);
        }, [
            selectorsSchema,
            selectOverrides,
            getIsOpenValueList,
            openDropdowns,
            classes.popoverCustomWide,
            t,
            handleChangeType,
            type,
            onClear,
        ]);

        const preComponent = useMemo(() => {
            return (
                <Fragment>
                    {advice && (
                        <Advice
                            type={adviceType}
                            className={classes.advice}
                            showIcon
                            showCollapse={false}
                        >
                            {advice}
                        </Advice>
                    )}
                    <div className={searchContainerClassName}>
                        <div className={searchInputClassName}>
                            {multiSchema && !selectorsSchema ? (
                                <SearchBar
                                    placeholder={placeholderSearch}
                                    typeOptions={typeOptions}
                                    type={type}
                                    onChangeType={handleChangeType()}
                                    allowMultipleTypes
                                    selectedTypesLiteral={t('label_number_selected')}
                                    selectedTypesPlaceholder={t('label_none_selected')}
                                    useAsSimpleSearch={true}
                                    forceStartFocused={inputFocus}
                                    onChange={(e) => onSearch(e?.target?.value || null)}
                                />
                            ) : (
                                <Input
                                    placeholder={placeholderSearch}
                                    onChange={onSearch}
                                    value={search}
                                    preComponent={<Icon name="search" color="currentColor" />}
                                    autoFocus={inputFocus}
                                    isFullWidth
                                    className={classes.searchInput}
                                />
                            )}
                        </div>
                        {renderSelectors}
                    </div>
                </Fragment>
            );
        }, [
            advice,
            adviceType,
            classes.advice,
            classes.searchInput,
            multiSchema,
            placeholderSearch,
            typeOptions,
            type,
            handleChangeType,
            t,
            onSearch,
            search,
            inputFocus,
            selectorsSchema,
            renderSelectors,
            searchContainerClassName,
            searchInputClassName,
        ]);

        const selectedFilters = useMemo(() => {
            let newSelected = getSelectedFilters(selected);

            return newSelected;
        }, [selected]);

        const disableResetButton = useMemo(() => {
            if (!multiSchema) return isEqual(defaultFilters, selectedFilters);

            const totalTypes = Object.keys(selected).length;
            const totalSelected = Object.values(selected).filter((item) => item.isSelected).length;

            let areTypesResetable;
            if (forceSelectAllTypes) {
                areTypesResetable = totalTypes !== totalSelected;
            } else {
                areTypesResetable = !totalSelected;
            }

            const totalInitialSelected = Object.values(initialSelected).filter(
                (item) => item.isSelected,
            ).length;

            return (
                isEqual(defaultFilters, selectedFilters) &&
                totalInitialSelected === totalSelected &&
                !areTypesResetable
            );
        }, [
            defaultFilters,
            selectedFilters,
            initialSelected,
            selected,
            forceSelectAllTypes,
            multiSchema,
        ]);

        const handleOnResetSelected = useCallback(() => {
            if (disableResetButton) return;
            onResetSelected && onResetSelected();
        }, [disableResetButton, onResetSelected]);

        return (
            <Modal
                className={classNames}
                overlayClassName={overlayClassName || classes.overlay}
                title={isLoading ? ' ' : title}
                isOpen={isOpen}
                size={size}
                useContentStaticHeight={true}
                onRequestClose={handleCancel}
                onCancel={!isLoading ? handleCancel : undefined}
                onConfirm={!isLoading ? handleConfirm : undefined}
                cancelText={cancelText}
                confirmText={confirmText}
                deleteText={deleteText}
                preComponent={!isLoading && preComponent}
                postComponent={!isLoading && postComponent}
                onDelete={handleOnResetSelected}
                classes={{
                    deleteButton: disableResetButton
                        ? classes.resetSelectedDisabled
                        : classes.resetSelected,
                }}
                overrides={useMemo(
                    () => ({
                        content: {
                            style: isLoading
                                ? {
                                      height: '100%',
                                  }
                                : {
                                      border: `1px solid ${theme.colors.grey[100]}`,
                                      height: '100%',
                                      borderRadius: '4px',
                                  },
                        },
                        ...overrides,
                    }),
                    [isLoading, overrides, theme],
                )}
            >
                {isLoading && (
                    <div className={classes.loader}>
                        <Loader size="massive" />
                    </div>
                )}
                {!isLoading && <div className={classes.optionsContainer}>{options}</div>}
            </Modal>
        );
    },
);

const schema = PropTypes.arrayOf(
    PropTypes.shape({
        label: PropTypes.string,
        id: PropTypes.string,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                id: PropTypes.string,
            }),
        ),
    }),
);

ModalOptions.propTypes = {
    advice: PropTypes.string,
    adviceType: PropTypes.oneOf(['error', 'info', 'success', 'warning']),
    className: PropTypes.string,
    isOpen: PropTypes.bool,
    onToggleModal: PropTypes.func,
    // Use one of those, schema or multischema
    schema,
    multiSchema: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            id: PropTypes.string,
            expanded: PropTypes.bool,
            defaultTypeState: PropTypes.bool,
            schema,
        }),
    ),
    selected: PropTypes.object,
    //For schema
    //Ex:
    // selected: {
    //     ['info']: {
    //         isSelected: true,
    //         options: ['name', 'status', 'environment'],
    //     },
    // }
    // For multiSchema
    // Ex:
    // selected: {
    //     ['group']: {
    //         isExpanded: true,
    //         isSelected: true,
    //         options: {
    //             ['subGroup']: {
    //                 isSelected: true,
    //                 options: ['name', 'status', 'environment']
    //             }
    //         }
    //     }
    // }
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    overrides: PropTypes.object,
    postComponent: PropTypes.node,
    overlayClassName: PropTypes.string,
    isLoading: PropTypes.bool,
};

export default ModalOptions;
